import { domAnimation, m } from 'framer-motion';
import type { CapiProductPage } from 'types/capi/pdp';

import { Button } from '~/components/Button';
import FramerMotion from '~/components/FramerMotion';
import useHasScrolledPast from '~/hooks/useHasScrolledPast';
import usePageData from '~/hooks/usePageData';

type Props = {
  children: React.ReactNode;
  hideOffset?: number;
  hideOnElementRef: React.RefObject<HTMLDivElement>;
  onSizeSelectorClick: () => void;
  selectedSize?: string;
  stockStatus: string;
};

const StickyCta = ({
  stockStatus,
  children,
  selectedSize,
  hideOffset = 0,
  hideOnElementRef,
  onSizeSelectorClick
}: Props) => {
  const { allow_size_selection } = usePageData<CapiProductPage>();

  const hideSticky = useHasScrolledPast(hideOnElementRef, hideOffset);

  const showSizeSelctor = allow_size_selection && stockStatus === 'in-stock';

  return (
    <FramerMotion feature={domAnimation}>
      <m.div
        data-cy="sticky-cta"
        initial={false}
        animate={hideSticky ? 'hidden' : 'visible'}
        variants={{
          hidden: { opacity: 0, y: 100 },
          visible: { opacity: 1, y: 0 }
        }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
        className="fixed -bottom-0.5 left-0 z-10 flex w-full items-stretch justify-center gap-2 rounded-t-md bg-black p-4 shadow lg:hidden"
      >
        {showSizeSelctor && (
          <SizeGridToggle
            selectedSize={selectedSize}
            onClick={onSizeSelectorClick}
          />
        )}

        <div className="grow">{children}</div>
      </m.div>
    </FramerMotion>
  );
};

const SizeGridToggle = ({
  onClick,
  selectedSize
}: {
  onClick: () => void;
  selectedSize?: string;
}) => {
  return (
    <Button
      onClick={onClick}
      variant={selectedSize ? 'black-cta' : 'green-cta'}
      className="whitespace-nowrap text-base"
      data-cy="size-selector-toggle-sticky"
    >
      {selectedSize ? `UK ${selectedSize}` : 'Your size'}
    </Button>
  );
};

export default StickyCta;
